import React, { useState, useRef, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/apiClient';
import RecipeTitle from './RecipeFields/RecipeTitle';
import RecipeCategory from './RecipeFields/RecipeCategory';
import Servings from './RecipeFields/Servings';
import IngredientLinesEditor from './RecipeFields/IngredientLinesEditor';
import CookingTime from './RecipeFields/CookingTime';
import Instructions from './RecipeFields/Instructions';
import SpecialNotes from './RecipeFields/SpecialNotes';
import Tooltip from '../FloatingWidgets/TooltipSystem';
import Signature from './RecipeFields/Signature';
import { AuthContext } from '../../contexts/AuthContext';
import { SaveContext } from '../../contexts/SaveContext';
import { useRecipe } from '../../contexts/RecipeContext';
import { useIngredientLines } from '../../contexts/IngredientLinesContext';
import RecipeCardPrintView from './PreviewCard';

export default function RecipeEditor({ recipeCardRef, forceBlank = false, hidePlaceholders = false, onSave, templateCode }) {
  const { id } = useParams();
  // console.log("🆕 Recipe ID:", id);
  const isNew = window.location.pathname === '/recipes/new/edit';
  // console.log("🆕 isNew:", isNew);
  const [recipeTitle, setRecipeTitle] = useState('');
  const [recipeCategory, setRecipeCategory] = useState('');
  const [servings, setServings] = useState('');
  const [cookingTime, setCookingTime] = useState('');
  const [ingredients, setIngredients] = useState([]);
  const [instructions, setInstructions] = useState([]);
  const [specialNotes, setSpecialNotes] = useState('');
  const [signature, setSignature] = useState('');
  const [backgroundImagePath, setBackgroundImagePath] = useState('');
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const [isSaved, setIsSaved] = useState(false);
  const { setRecipe } = useRecipe();
  const { ingredientLines, replaceServerLines } = useIngredientLines();
  const [selectedDesign, setSelectedDesign] = useState(() => {
    try {
      const draft = JSON.parse(localStorage.getItem('draft_recipe'));
      if (draft?.designCode) return draft.designCode;
    } catch {}
  
    const accessibleTemplates = auth.user?.templates || [];
    if (accessibleTemplates.length > 0) return accessibleTemplates[0]; // pick first accessible
  
    return ''; // fallback to blank — prompt user to choose
  });

  const [draftLoaded, setDraftLoaded] = useState(false); // 🆕 flag
  const [ready, setReady] = useState(false); // loading state for editor readiness

  // 🧼 Reset all states before loading a new recipe
  useEffect(() => {
    setReady(false); // ⛔ Prevent premature render
    setRecipeTitle('');
    setRecipeCategory('');
    setServings('');
    setCookingTime('');
    setIngredients([]);
    setInstructions([]);
    setSpecialNotes('');
    setSignature('');
    setBackgroundImagePath('');

     replaceServerLines([]); // ✅ ADD THIS
}, [id, templateCode, replaceServerLines]); // ✅ update deps

  // Load template background image and recipe data if editing an existing recipe or a draft
  useEffect(() => {
    const loadTemplateBackground = async (designCode) => {
      try {
        const { data: template } = await api.get(`/api/templates/${designCode}`);
        const imagePath = template.backgroundImage || template.previewImage || '';
        setBackgroundImagePath(imagePath);

        if (imagePath) {
          const img = new Image();
          img.onload = () => setReady(true);
          img.onerror = () => setReady(true);
          img.src = `${process.env.REACT_APP_API_BASE_URL}/${imagePath.replace(/^\/+/, '')}`;
        } else {
          setReady(true); // no image to load
        }
      } catch (err) {
        console.warn("⚠️ Failed to load template:", err);
        setReady(true); // still allow app to render
      }
    };
  
    if (templateCode) {
      // Admin bulk export: render this template only, no recipe/draft involved.
      setSelectedDesign(templateCode);
      loadTemplateBackground(templateCode);
      return;
    }

    if (isNew) {
      const draft = localStorage.getItem('draft_recipe');
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          setRecipeTitle(parsed.title || '');
          setSelectedDesign(parsed.designCode || '');
          setIngredients(parsed.ingredients || []);
          setInstructions(parsed.instructions || []);
          setSpecialNotes(parsed.notes || '');
          setRecipeCategory(parsed.category || '');
          setServings(parsed.servings || '');
          setCookingTime(Number(parsed.cookingTime ?? ''));
          setSignature(parsed.signature || '');
  
          if (parsed.designCode) {
            loadTemplateBackground(parsed.designCode);
          }
  
          console.log("📥 Draft recipe.designCode:", parsed.designCode);
        } catch (err) {
          console.error("⚠️ Failed to load draft:", err);
        }
      }
        setDraftLoaded(true);
    } else {
      const liveRecipe = localStorage.getItem('current_recipe');
      if (liveRecipe) {
        try {
          const parsed = JSON.parse(liveRecipe);
          setRecipeTitle(parsed.title || '');
          setRecipeCategory(parsed.category || '');
          setServings(parsed.yield || '');
          setCookingTime(parsed.cook_time || '');
          setIngredients(parsed.ingredients ? parsed.ingredients.split('\n') : []);
          setInstructions(parsed.instructions ? parsed.instructions.split('\n') : []);
          setSpecialNotes(parsed.notes || '');
          setSelectedDesign(parsed.design_code || '');
          if (parsed.design_code) loadTemplateBackground(parsed.design_code);

          // console.log('📦 Loaded recipe from localStorage current_recipe.');
        } catch (err) {
          console.error('⚠️ Failed to parse current_recipe from localStorage:', err);
        }
      }

      // ✅ Always get latest from backend to override stale cache
      // console.log("🚀 Fetching recipe from backend with ID:", id);

      api.get(`/api/recipes/${id}`)
        .then(async (res) => {
          const recipe = res.data;

          // ✅ Always load recipe fields (so instructions never disappear)
          setRecipeTitle(recipe?.title || '');
          setRecipeCategory(recipe?.category || '');
          setServings(recipe?.yield || '');
          setCookingTime(Number(recipe?.cook_time ?? ''));

          if (typeof recipe.instructions === 'string') {
            setInstructions(recipe.instructions.split('\n'));
          } else if (Array.isArray(recipe.instructions)) {
            setInstructions(recipe.instructions);
          } else {
            setInstructions([]);
          }

          setSpecialNotes(typeof recipe.notes === 'string' ? recipe.notes : '');
          setSignature(recipe.signature || '');
          setSelectedDesign(recipe?.design_code || '');

          // ✅ Load ingredient lines WITHOUT blocking recipe render
          try {
            const linesRes = await api.get(`/api/ingredient_lines/recipes/${id}/ingredients`);
            const lines = linesRes.data?.lines ?? linesRes.data ?? [];
            replaceServerLines(lines);
            // console.log("📥 Hydrated ingredient lines:", lines);
          } catch (e) {
            console.warn("⚠️ Ingredient lines fetch failed (recipe still loaded):", e);
            replaceServerLines([]);
          }

          // ✅ Load template background (existing logic)
          if (recipe?.design_code) {
            try {
              const { data: template } = await api.get(`/api/templates/${recipe.design_code}`);
              const imagePath = template.backgroundImage || template.previewImage || '';
              setBackgroundImagePath(imagePath);

              if (imagePath) {
                const img = new Image();
                img.onload = () => setReady(true);
                img.onerror = () => setReady(true);
                img.src = `${process.env.REACT_APP_API_BASE_URL}/${imagePath.replace(/^\/+/, '')}`;
              } else {
                setReady(true);
              }
            } catch (err) {
              console.warn("⚠️ Failed to load template background:", err);
              setReady(true);
            }
          } else {
            setReady(true);
          }

          // console.log("📥 Backend recipe.design_code:", recipe?.design_code);

          // ✅ Inject into RecipeContext
          setRecipe({ ...recipe });
          setDraftLoaded(true);
        })
        .catch((err) => {
          console.error("❌ Failed to fetch recipe:", err);
          setReady(true);
          setDraftLoaded(true);
        });
    }

  }, [id, templateCode]);

  useEffect(() => {
    const handleAdd = (e) => {
      const ingredient = e.detail;
      setIngredients(prev => {
        const max = 9;
        const next = [...prev];
        for (let i = 0; i < max; i++) {
          if (!next[i] || next[i] === '') {
            next[i] = ingredient;
            break;
          }
        }
        return next;
      });
    };
  
    window.addEventListener('add-ingredient', handleAdd);
    return () => window.removeEventListener('add-ingredient', handleAdd);
  }, []);

  // Autosave on changes
  useEffect(() => {
    if (!draftLoaded) return; // 🚫 don't autosave too early
    if (!isNew) return; // ✅ only autosave draft for new recipe flow
  
    const draft = {
      title: recipeTitle,
      designCode: selectedDesign,
      backgroundImage: backgroundImagePath,
      ingredients,
      instructions,
      notes: specialNotes,
      category: recipeCategory,
      servings,
      cookingTime,
      signature
    };
    localStorage.setItem('draft_recipe', JSON.stringify(draft));
  }, [
    draftLoaded, // ✅ dependency to wait until draft is loaded
    isNew,
    recipeTitle,
    selectedDesign,
    backgroundImagePath,
    ingredients,
    instructions,
    specialNotes,
    recipeCategory,
    servings,
    cookingTime,
    signature
  ]);

  const backgroundImageURL = backgroundImagePath
  ? `${process.env.REACT_APP_API_BASE_URL}/${backgroundImagePath.replace(/^\/+/, '')}`
  : '';

  const raw_ingredients_text = (ingredientLines || [])
    .map((l) => String(l?.raw_text ?? "").trim())
    .filter(Boolean)
    .join("\n");

  const structuredIngredients = (ingredientLines || [])
    .filter((l) => l?.ingredient_id && l?.rit_payload?.quantity != null && l?.rit_payload?.unit)
    .map((l) => ({
      ingredient_id: Number(l.ingredient_id),
      quantity: Number(l.rit_payload.quantity),
      unit: String(l.rit_payload.unit),
      note: Array.isArray(l?.rit_payload?.notes) ? l.rit_payload.notes.join(", ") : "",
    }));

  const handleSave = async () => {
    const hasAnyIngredientLine =
      (ingredientLines || []).some((l) => String(l?.raw_text ?? "").trim().length > 0);

    // console.log("SAVE DEBUG", {
    //   title: recipeTitle,
    //   ingredientLines,
    //   ingredientLinesLen: ingredientLines?.length,
    //   hasAnyIngredientLine,
    //   instructions,
    //   instructionsLen: instructions?.length,
    //   raw_ingredients_text,
    //   structuredIngredients,
    // });

    if (!recipeTitle.trim() || !hasAnyIngredientLine || !instructions.length) {
      alert("Please fill in the title, ingredients, and instructions.");
      return;
    }

    const payload = {
      title: recipeTitle.trim(),
      category: recipeCategory,
      yield: servings,
      prep_time: null,
      cook_time: cookingTime,

      raw_ingredients_text,
      ingredients: structuredIngredients,

      instructions: instructions.join('\n'),
      notes: specialNotes,
      design_code: selectedDesign,
      signature,
    };


    // console.log("📦 Payload:", payload);
  
    try {
      // ✅ Always PUT (init already created the recipe)
      await api.put(`/api/recipes/${id}`, payload);
      setIsSaved(true);
      if (isNew) {
        localStorage.removeItem('draft_recipe');
      }
    } catch (err) {
      console.error("❌ Save failed:", err);
      setIsSaved(false);
    }
  };

  // Connect save ref from parent (EditorLayout)
  useEffect(() => {
    if (typeof onSave === 'object' && onSave !== null) {
      onSave.current = handleSave;
    }
  }, [onSave, handleSave]);

  // console.log("🖼️ Loaded backgroundImagePath:", backgroundImagePath);
  // console.log("🖼️ Resolved image URL:", backgroundImageURL);

  if (!ready) {
    return (
      <div className="flex justify-center items-center w-full h-[90vh]">
        <div className="w-[600px] h-[900px] bg-white border border-light-gray shadow rounded-md p-4 animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-3/4" />
          <div className="h-4 bg-slate-100 rounded w-1/2" />
          <div className="h-4 bg-slate-100 rounded w-5/6" />
          <div className="h-4 bg-slate-100 rounded w-4/6" />
          <div className="h-[600px] bg-slate-50 rounded" />
        </div>
      </div>
    );
  }
  return (
      <div className={`flex justify-center items-center w-full overflow-x-hidden ${forceBlank ? '' : 'px-2 py-4'}`}>
        <div className="w-full max-w-[600px] aspect-[2/3] relative">
          <div
            ref={recipeCardRef}
            data-testid={forceBlank ? 'blank-recipe-export-card' : 'filled-recipe-export-card'}
            // className="
            //   absolute top-0 left-0 w-full h-full
            //   p-[10.7%_6.1%_10%_6.1%]  /* matches 65px 37px 90px at full scale */
            //   grid 
            //   grid-rows-[10%_8%_76%_6%]  /* matches 85, 60, 555, 60 from 900px */
            //   grid-cols-1 
            //   rounded-[15px] 
            //   font-serif 
            //   // border-2 border-dashed border-red-500 
            //   bg-cover bg-center overflow-hidden
            // "
            className="
              absolute top-0 left-0 w-full h-full
              p-[10.7%_6.1%_10%_6.1%]
              grid grid-rows-[10%_8%_76%_6%] grid-cols-1 gap-[0.5%]
              rounded-[15px] font-serif
              bg-cover bg-center overflow-hidden
            "
            style={{
              backgroundImage: backgroundImageURL ? `url(${backgroundImageURL})` : 'none',
              backgroundColor: backgroundImageURL ? 'transparent' : '#fefefe',
            }}
          >
          {/* 🟦 Title */}
          {/* <div className="border-2 border-dashed border-blue-500 row-start-1 h-full w-full px-[3%] py-[2%] flex items-center justify-center"> */}
          <div className="row-start-1 h-full w-full px-[3%] py-[2%] flex items-center justify-center frosted-title">
            <div className="w-full h-full text-[0.9em] md:text-[1em]">
              <RecipeTitle title={forceBlank ? '' : recipeTitle} onChange={setRecipeTitle} />
              {/* <Tooltip content="Enter a descriptive recipe title." /> */}
            </div>
          </div>

          {/* 🟩 Details */}
          {/* <div className="border-2 border-dashed border-green-500 row-start-2 h-full w-full flex justify-between items-center px-[4%] py-[2%] gap-[2%] text-[0.9em] md:text-[1em]"> */}
          <div className="row-start-2 h-full w-full flex justify-between items-center px-[4%] py-[2%] gap-[2%] text-[0.9em] md:text-[1em] frosted-details">
            <div className="flex-[1.3] h-full text-center flex flex-col justify-center">
              <RecipeCategory category={forceBlank ? '' : recipeCategory} onChange={setRecipeCategory} />
              {/* <Tooltip content="Select a category." /> */}
            </div>
            <div className="flex-[1] h-full text-center flex flex-col justify-center">
              <Servings servings={forceBlank ? '' : servings} onChange={setServings} />
              {/* <Tooltip content="Enter servings." /> */}
            </div>
            <div className="flex-[1.3] h-full text-center flex flex-col justify-center">
              <CookingTime cookingTime={forceBlank ? '' : cookingTime} onChange={setCookingTime} />
              {/* <Tooltip content="Enter cook time." /> */}
            </div>
          </div>

          {/* 🟪 Ingredients + Instructions */}
          {/* <div className="border-2 border-dashed border-purple-500 row-start-3 grid grid-cols-[39%_59%] gap-[2%] h-full"> */}
          <div className="row-start-3 grid grid-cols-[39%_59%] gap-[2%] h-full bg-transparent">
            {/* 🟪 Left Column: Ingredients & Notes */}
              <div className="grid grid-rows-[80%_19.01%] gap-[1%] ml-[1%] h-full w-full border border-transparent rounded-[5px] overflow-hidden">
                {/* 🍴 Ingredients Section */}
                <div className="h-full w-full frosted-block p-2 overflow-hidden">
                    <IngredientLinesEditor readOnly={forceBlank} forceBlank={forceBlank} />
                    {/* <Tooltip content="List ingredients here." /> */}
                </div>

                {/* 📝 Special Notes Section */}
                <div className="h-full w-full frosted-block p-2 overflow-hidden">
                  <SpecialNotes
                    notes={forceBlank ? '' : specialNotes}
                    onChange={setSpecialNotes}
                    placeholder={hidePlaceholders ? '' : undefined}
                  />
                    {/* <Tooltip content="Enter special notes." /> */}
                </div>
            </div>

            {/* Right Column: Instructions */}
            <div className="frosted-block mr-[2%] h-full w-full p-[2%]">
              <Instructions
                instructions={forceBlank ? [] : instructions}
                hidePlaceholders={hidePlaceholders}
                onAddStep={() => setInstructions([...instructions, ''])}
                onRemoveStep={(index) => setInstructions(instructions.filter((_, i) => i !== index))}
                onStepChange={(index, value) => {
                  const updated = [...instructions];
                  updated[index] = value;
                  setInstructions(updated);
                }}
              />
              {/* <Tooltip content="Add cooking steps here." /> */}
            </div>
          </div>

          {/* 🟣 Footer */}
          {/* <div className="border-2 border-dashed border-pink-500 row-start-4 flex justify-center items-center px-[2%] pb-[1%] h-full"> */}
          <div className="row-start-4 flex justify-center items-center px-[2%] pb-[1%] h-full frosted-footer">
            <Signature defaultName={forceBlank ? '' : signature} onNameChange={setSignature} />
          </div>
        </div>

        {/* 🖨️ Hidden Print View (not used by the admin template export, which has no recipe id) */}
        {!templateCode && (
        <div
          id="printViewWrapper"
          className="w-[600px] h-[900px] hidden"
        >
          <RecipeCardPrintView
            recipeData={{
              title: forceBlank ? '' : recipeTitle,
              category: forceBlank ? '' : recipeCategory,
              servings: forceBlank ? '' : servings,
              cookingTime: forceBlank ? '' : cookingTime,
              ingredients: forceBlank ? [] : ingredients,
              instructions: forceBlank ? [] : instructions,
              notes: forceBlank ? '' : specialNotes,
              timestamp: new Date().toLocaleString(),
            }}
          />
        </div>
        )}
      </div>
      {/* <div className="mt-6 text-center">
        <Button intent="primary" onClick={handleSaveRecipe}>
          Save Recipe
        </Button>
      </div> */}
    </div>
  );
}
